import { pb } from "./pocketbase";
import { bomService } from "./bomService";

export const stockProductionService = {
  // --- 1. LANÇAR LOTE (Nascimento das peças) ---
  async launchBatch(sku, qty, user, loteId = null) {
    // 1. Busca a receita BOM para o SKU
    let recipe = [];
    try {
      recipe = await bomService.getRecipe(sku);
    } catch (e) {
      console.error("[BOM] Erro ao buscar receita para SKU:", sku, e);
    }

    // 2. Filtra insumos que pertencem ao setor "oficina"
    const oficinaRecipe = recipe.filter((item) => {
      const sector = item.expand?.supply_id?.sector || "";
      return sector.trim().toLowerCase() === "oficina";
    });

    const bomDetails = [];

    // 3. Se houver insumos da oficina, debita o estoque
    if (oficinaRecipe.length > 0) {
      const debitPromises = oficinaRecipe.map(async (item) => {
        try {
          const supplyId = item.supply_id;
          const qtyPerUnit = Number(item.quantity_per_unit) || 0;
          const totalDeduction = qty * qtyPerUnit;

          // Busca o saldo atualizado para evitar problemas de concorrência
          const supplyRecord = await pb.collection("supplies").getOne(supplyId);
          const currentQty = Number(supplyRecord.current_quantity) || 0;
          const newQty = currentQty - totalDeduction;

          await pb.collection("supplies").update(supplyId, {
            current_quantity: newQty,
          });

          // Registra o detalhe do insumo debitado para cada peça individual
          bomDetails.push({
            supply_id: supplyId,
            supply_name: supplyRecord.name || item.expand?.supply_id?.name || "Insumo",
            qty: qtyPerUnit,
          });
        } catch (err) {
          console.error(`[BOM] Erro ao debitar insumo ${item.supply_id}:`, err);
        }
      });

      await Promise.all(debitPromises);
    }

    // 4. Cria 'qty' documentos novos em inventory_items
    const promises = [];
    for (let i = 0; i < qty; i++) {
      const historyList = [
        {
          id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          action: "criado_pe",
          user: user || "Sistema",
          date: new Date().toISOString(),
          text: "Lote criado na fábrica",
          type: "system"
        }
      ];

      // Se houve débito de BOM, inclui no histórico da peça
      if (bomDetails.length > 0) {
        historyList.push({
          id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          action: "bom_debited",
          user: "Sistema",
          date: new Date().toISOString(),
          text: `Insumos da Oficina debitados: ${bomDetails.map(d => `${d.qty}x ${d.supply_name}`).join(", ")}`,
          details: bomDetails,
          type: "system"
        });
      }

      promises.push(
        pb.collection("inventory_items").create({
          sku: sku.toUpperCase().trim(),
          status: "pe_solicitado", // Status inicial
          isPE: true, // Flag importante: É Produção de Estoque
          history: historyList,
          dateIn: new Date().toISOString(),
          addedBy: user || "Sistema",
          lote: loteId || null,
          situacao_fluxo: "ativo",
          full_data: {
            prioridade: "Planejado"
          }
        })
      );
    }

    await Promise.all(promises);
  },

  // --- 2. AVANÇAR ETAPA (Em Massa) ---
  async advanceBatchStatus(itemIds, newStatus, user) {
    const STANDARD_DB_STATUSES = new Set([
      "pe_solicitado",
      "pe_imprimindo",
      "pe_cura",
      "pe_fundicao",
      "pe_fundidos",
      "pe_conferencia",
      "pe_acabamento",
      "pe_cravacao",
      "pe_polimento",
      "pe_finalizacao",
      "pe_interceptado",
      "in_stock",
      "adjusted_out",
      "sold"
    ]);

    // Itera e atualiza um por um para poder preservar e atualizar o histórico de cada item
    const promises = itemIds.map(async (id) => {
      try {
        const item = await pb.collection("inventory_items").getOne(id);
        const currentHistory = Array.isArray(item.history) ? item.history : [];
        const oldStatus = item.reason && item.reason.startsWith("pe_") ? item.reason : item.status;

        let statusText = `Etapa alterada para: ${newStatus}`;
        let actionName = "status_change";

        if (newStatus === "in_stock") {
          statusText = "Adicionado ao estoque físico real";
          actionName = "finalizado_pe";
        } else if (newStatus === "pe_revisao") {
          statusText = "Lote retornado para Revisão";
          actionName = "status_change";
        }

        const newEvent = {
          id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          action: actionName,
          user: user || "Sistema",
          date: new Date().toISOString(),
          text: statusText,
          from: oldStatus,
          to: newStatus,
          type: "system"
        };

        const updatedHistory = [...currentHistory, newEvent];
        const updatePayload = {
          history: updatedHistory,
        };

        if (newStatus === "in_stock") {
          updatePayload.status = "in_stock";
          updatePayload.isPE = false; // Remove a flag de PE, agora é estoque real
          updatePayload.situacao_fluxo = "concluido";
          updatePayload.reason = ""; // Limpa custom
        } else {
          updatePayload.situacao_fluxo = item.situacao_fluxo === "interceptado" ? "interceptado" : "ativo";
          if (STANDARD_DB_STATUSES.has(newStatus)) {
            updatePayload.status = newStatus;
            updatePayload.reason = ""; // Limpa custom
          } else {
            // Se for status customizado
            updatePayload.status = "pe_solicitado";
            updatePayload.reason = newStatus;
          }
        }

        return await pb.collection("inventory_items").update(id, updatePayload);
      } catch (err) {
        console.error(`Erro ao avançar status do item ${id}:`, err);
        throw err;
      }
    });

    await Promise.all(promises);
  },

  // --- 3. ADICIONAR MURAL COMENTÁRIO (Timeline) ---
  async addComment(itemId, text, authorName) {
    try {
      const item = await pb.collection("inventory_items").getOne(itemId);
      const currentHistory = Array.isArray(item.history) ? item.history : [];

      const newEvent = {
        id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        action: "comentario",
        user: authorName || "Usuário",
        date: new Date().toISOString(),
        text: text,
        type: "user"
      };

      return await pb.collection("inventory_items").update(itemId, {
        history: [...currentHistory, newEvent]
      });
    } catch (err) {
      console.error(`Erro ao adicionar comentário ao item ${itemId}:`, err);
      throw err;
    }
  },

  // --- 4. CANCELAR/EXCLUIR (Apenas Rascunhos) ---
  async deleteBatch(itemIds) {
    const promises = itemIds.map(async (id) => {
      try {
        const item = await pb.collection("inventory_items").getOne(id);
        
        // REGRA DE SEGURANÇA: Só pode deletar fisicamente se pertencer a lote em Rascunho (ou sem lote)
        if (item.lote) {
          try {
            const lote = await pb.collection("estoque_lotes").getOne(item.lote);
            if (lote.status !== "rascunho") {
              throw new Error(`Não é possível excluir itens do lote lançado "${lote.codigo}". Use a suspensão.`);
            }
          } catch (loteErr) {
            console.warn("[deleteBatch] Erro ao checar status do lote, impedindo exclusão para segurança:", loteErr);
            throw new Error("Não foi possível validar o lote da peça. Exclusão abortada por segurança.");
          }
        }

        // Tenta repor o BOM se aplicável
        const history = Array.isArray(item.history) ? item.history : [];
        const debitedEvent = history.find(h => h.action === "bom_debited");

        if (debitedEvent && Array.isArray(debitedEvent.details)) {
          const restorePromises = debitedEvent.details.map(async (detail) => {
            try {
              const supplyId = detail.supply_id;
              const qtyToRestore = Number(detail.qty) || 0;

              if (qtyToRestore > 0) {
                const supplyRecord = await pb.collection("supplies").getOne(supplyId);
                const currentQty = Number(supplyRecord.current_quantity) || 0;
                await pb.collection("supplies").update(supplyId, {
                  current_quantity: currentQty + qtyToRestore
                });
              }
            } catch (restoreErr) {
              console.warn(`[BOM] Não foi possível repor insumo ${detail.supply_id} (talvez deletado):`, restoreErr);
            }
          });
          await Promise.all(restorePromises);
        }
      } catch (e) {
        console.error(`[BOM] Erro ao processar item ${id} para reposição de BOM:`, e);
        throw e;
      }

      // Deleta o item fisicamente
      return await pb.collection("inventory_items").delete(id);
    });

    await Promise.all(promises);
  },

  // --- 4.1 SUSPENDER PEÇA (Substitui exclusão física para lotes em produção) ---
  async suspendItem(itemId, motivoId, observacoes, user) {
    try {
      const item = await pb.collection("inventory_items").getOne(itemId);
      const currentHistory = Array.isArray(item.history) ? item.history : [];

      // 1. Devolve insumos BOM se houver registro de débito
      const debitedEvent = currentHistory.find(h => h.action === "bom_debited");
      if (debitedEvent && Array.isArray(debitedEvent.details)) {
        const restorePromises = debitedEvent.details.map(async (detail) => {
          try {
            const supplyId = detail.supply_id;
            const qtyToRestore = Number(detail.qty) || 0;

            if (qtyToRestore > 0) {
              const supplyRecord = await pb.collection("supplies").getOne(supplyId);
              const currentQty = Number(supplyRecord.current_quantity) || 0;
              await pb.collection("supplies").update(supplyId, {
                current_quantity: currentQty + qtyToRestore
              });
            }
          } catch (restoreErr) {
            console.warn(`[BOM-Suspensão] Não foi possível repor insumo ${detail.supply_id}:`, restoreErr);
          }
        });
        await Promise.all(restorePromises);
      }

      // 2. Adiciona evento de suspensão ao histórico
      const newEvent = {
        id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        action: "suspenso_pe",
        user: user || "Sistema",
        date: new Date().toISOString(),
        text: `Peça suspensa da produção. Motivo: ${motivoId}. Obs: ${observacoes || ""}`,
        details: { motivoId, observacoes },
        type: "system"
      };

      // 3. Atualiza o status e situacao_fluxo no banco
      return await pb.collection("inventory_items").update(itemId, {
        situacao_fluxo: "suspenso",
        history: [...currentHistory, newEvent]
      });
    } catch (err) {
      console.error(`Erro ao suspender item ${itemId}:`, err);
      throw err;
    }
  },

  // --- 4.2 REATIVAR/DESUSPENDER PEÇA ---
  async reactivateItem(itemId, user) {
    try {
      const item = await pb.collection("inventory_items").getOne(itemId);
      const currentHistory = Array.isArray(item.history) ? item.history : [];

      // 1. Tenta encontrar o último evento de bom_debited no histórico
      const debitedEvent = currentHistory.find(h => h.action === "bom_debited");
      const bomDetails = [];

      if (debitedEvent && Array.isArray(debitedEvent.details)) {
        const debitPromises = debitedEvent.details.map(async (detail) => {
          try {
            const supplyId = detail.supply_id;
            const qtyToDeduct = Number(detail.qty) || 0;

            if (qtyToDeduct > 0) {
              const supplyRecord = await pb.collection("supplies").getOne(supplyId);
              const currentQty = Number(supplyRecord.current_quantity) || 0;
              await pb.collection("supplies").update(supplyId, {
                current_quantity: currentQty - qtyToDeduct
              });
              bomDetails.push(detail);
            }
          } catch (debitErr) {
            console.warn(`[BOM-Reativação] Não foi possível debitar insumo ${detail.supply_id}:`, debitErr);
          }
        });
        await Promise.all(debitPromises);
      }

      // 2. Adiciona evento de reativação ao histórico
      const newEvent = {
        id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        action: "reativado_pe",
        user: user || "Sistema",
        date: new Date().toISOString(),
        text: `Peça reativada/retornada para produção.`,
        type: "system"
      };

      if (bomDetails.length > 0) {
        newEvent.text += ` Insumos da Oficina redebitados: ${bomDetails.map(d => `${d.qty}x ${d.supply_name}`).join(", ")}`;
      }

      // 3. Atualiza o status e situacao_fluxo no banco
      return await pb.collection("inventory_items").update(itemId, {
        situacao_fluxo: "ativo",
        history: [...currentHistory, newEvent]
      });
    } catch (err) {
      console.error(`Erro ao reativar item ${itemId}:`, err);
      throw err;
    }
  },

  // --- 5. INTERCEPTAR ITEM (Vincula a pedido) ---
  async interceptItem(itemId, orderNumber, customerName) {
    const item = await pb.collection("inventory_items").getOne(itemId);
    const currentHistory = Array.isArray(item.history) ? item.history : [];

    const newEvent = {
      id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      action: "interceptado_para_pedido",
      user: "Sistema",
      date: new Date().toISOString(),
      text: `Vinculado ao pedido ${orderNumber} de ${customerName}`,
      type: "system"
    };

    await pb.collection("inventory_items").update(itemId, {
      status: "pe_interceptado", // Status travado
      isPE: true, // Ainda é PE, mas não está disponível
      situacao_fluxo: "interceptado",
      linkedOrderNumber: orderNumber,
      linkedCustomer: customerName,
      history: [...currentHistory, newEvent],
    });
  },

  // --- 6. CONVERTER PEÇA PARA AVULSA (Remove vínculo de lote cancelado/suspenso) ---
  async convertToAvulso(itemId, user) {
    const item = await pb.collection("inventory_items").getOne(itemId);
    const currentHistory = Array.isArray(item.history) ? item.history : [];
    const wasSuspended = item.situacao_fluxo === "suspenso";

    // 1. Se a peça estava suspensa por causa do lote, redebitamos os insumos BOM ao reativá-la
    const bomDetails = [];
    if (wasSuspended) {
      const debitedEvent = currentHistory.find(h => h.action === "bom_debited");
      if (debitedEvent && Array.isArray(debitedEvent.details)) {
        const debitPromises = debitedEvent.details.map(async (detail) => {
          try {
            const supplyId = detail.supply_id;
            const qtyToDeduct = Number(detail.qty) || 0;

            if (qtyToDeduct > 0) {
              const supplyRecord = await pb.collection("supplies").getOne(supplyId);
              const currentQty = Number(supplyRecord.current_quantity) || 0;
              await pb.collection("supplies").update(supplyId, {
                current_quantity: currentQty - qtyToDeduct
              });
              bomDetails.push(detail);
            }
          } catch (debitErr) {
            console.warn(`[BOM-ConversãoAvulso] Não foi possível debitar insumo ${detail.supply_id}:`, debitErr);
          }
        });
        await Promise.all(debitPromises);
      }
    }

    const newEvent = {
      id: `pe_evt_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      action: "desvinculado_lote",
      user: user || "Sistema",
      date: new Date().toISOString(),
      text: "Vínculo de lote removido. Peça reativada e prossegue como Avulsa.",
      type: "system"
    };

    if (bomDetails.length > 0) {
      newEvent.text += ` Insumos da Oficina redebitados: ${bomDetails.map(d => `${d.qty}x ${d.supply_name}`).join(", ")}`;
    }

    return await pb.collection("inventory_items").update(itemId, {
      lote: "", // Remove o ID do lote
      situacao_fluxo: "ativo", // Reativa a peça no fluxo normal
      history: [...currentHistory, newEvent]
    });
  },

  // Helper para verificar se está disponível
  isAvailable(item) {
    return item.isPE === true && item.status !== "pe_interceptado" && item.situacao_fluxo !== "suspenso";
  },
};
